import {types, flow, getEnv} from "mobx-state-tree";
import intl from "react-intl-universal";


import {GQLQueryProvider} from "./GQLQueryProvider";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";


import {snackBarRef} from "../snackbarRef";


type QueryProviderEnv = {
    queryProvider?: GQLQueryProvider;
    getQueryProvider?: (node: any) => GQLQueryProvider | undefined;
};


export const getGqlModelBase = <T extends Object>() => {
    return types.model()
        .props({
            isNew: types.optional(types.boolean, false),
            hasChanges: types.optional(types.boolean, false),
            isSaving: types.optional(types.boolean, false),
            lastSavedObject: types.optional(types.frozen<T>(), {} as T),
            error: types.optional(types.maybeNull(types.frozen()), null)
        })
        .actions(self => ({
            proceedSaveResults(obj: T) {
                // @ts-ignore
                self.lastSavedObject = obj;
            },
            setHasChange(hasChanges: boolean) {
                self.hasChanges = hasChanges
            },
            resetChanges() {
                for (const fieldName in self.lastSavedObject) {
                    if (self.lastSavedObject.hasOwnProperty(fieldName)) {
                        // @ts-ignore
                        self[fieldName] = self.lastSavedObject[fieldName];
                    }
                }
                self.hasChanges = false;
            },
        }))
        .actions(self => ({
            setLastSavedObject(newCopy: T) {
                /* @ts-ignore */
                self.lastSavedObject = {...newCopy};
            }
        }))
        .actions(self => {
            const save = flow(function* () {
                self.isSaving = true;
                try {
                    let envQueryProvider: GQLQueryProvider | undefined;
                    const env = getEnv<QueryProviderEnv>(self);
                    envQueryProvider = env?.queryProvider;
                    if (!envQueryProvider && env?.getQueryProvider) {
                        envQueryProvider = env.getQueryProvider(self);
                    }

                    if (!envQueryProvider) {
                        throw new Error('GQLQueryProvider instance is not available in the environment');
                    }

                    self.proceedSaveResults(yield envQueryProvider.save(self as unknown as T));
                    self.hasChanges = false;
                    self.isNew = false;
                    self.isSaving = false;
                } catch (error) {
                    // ... including try/catch error handling
                    self.error = error;
                    const enqueueSnackbar = snackBarRef.current!.enqueueSnackbar;
                    enqueueSnackbar({
                        message: intl.get('common.request.fail').d('Failed to fetch. Please try again or contact us.') + ' ' + (error as any).message,
                        variant: 'error',
                    });
                    logErrorToSentry(error as Error);
                    self.isSaving = false;
                }
                return self;
            });

            return {
                save,
            }
        });
}
